import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { missionApi, type MissionPartyInfo, type MissionSummary } from '../../api/missionApi';
import { useCombatStore } from '../../store/combatStore';
import { useTranslation } from '../../i18n/useTranslation';
import base from './PartyPortalModal.module.css';
import styles from './MissionPortalModal.module.css';

// ─────────────────────────────────────────────────────────────────────────────
// MissionPortalModal — aberto ao clicar num Portal de Missão do Hub.
// Lista as missões do portal (fixa → uma só; "jogador escolhe" → todas as
// ativas), com custo e de onde o jogador começa (nível 1, checkpoint ou a
// tentativa em andamento). Entrar → /play no combate do nível.
// Em grupo (2+) só o líder inicia — os outros membros vão junto pelo poll do
// grupo, ou retomam por aqui uma tentativa já em andamento.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  token: string;
  /** Chave da célula do portal no HubConfig ("X_Y"). */
  portalKey: string;
  onClose: () => void;
}

export default function MissionPortalModal({ token, portalKey, onClose }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [missions, setMissions] = useState<MissionSummary[] | null>(null);
  const [entering, setEntering] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [party, setParty] = useState<MissionPartyInfo | null>(null);

  useEffect(() => {
    missionApi.list(token, portalKey)
      .then(r => { setMissions(r.missions); setParty(r.party); })
      .catch(e => { setMissions([]); setError((e as Error).message); });
  }, [token, portalKey]);

  const enter = async (m: MissionSummary) => {
    if (entering) return;
    setEntering(m.id);
    setError(null);
    try {
      const res = await missionApi.enter(token, m.id);
      useCombatStore.getState().resetCombat();
      navigate(`/play?combat_id=${res.combat_id}&entity_id=${res.player_entity_id}`);
    } catch (e) {
      setError((e as Error).message);
      setEntering(null);
    }
  };

  const costLabel = (m: MissionSummary) => {
    const parts = [];
    if (m.energy_cost > 0) parts.push(`⚡ ${m.energy_cost}`);
    if (m.coin_cost > 0) parts.push(`💰 ${m.coin_cost}`);
    return parts.length ? parts.join(' · ') : t('mission.portal.free');
  };

  return createPortal(
    <div className={base.overlay} onClick={onClose}>
      <div className={base.box} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={t('mission.portal.title')}>
        <div className={base.header}>
          <h3 className={base.title}>🔮 {t('mission.portal.title')}</h3>
          <button className={base.closeBtn} onClick={onClose} aria-label={t('common.close')}>✕</button>
        </div>

        {missions === null && <p className={base.hint}>{t('mission.portal.loading')}</p>}
        {party && <p className={base.hint}>{t('mission.portal.team').replace('{n}', String(party.size))}</p>}
        {missions?.length === 0 && !error && <p className={base.hint}>{t('mission.portal.none')}</p>}

        <ul className={styles.list}>
          {missions?.map(m => (
            <li key={m.id} className={styles.item}>
              <div className={styles.info}>
                <span className={styles.name}>{m.name}</span>
                <span className={styles.meta}>
                  {m.levels_count} {t('mission.portal.levels')} · {costLabel(m)}
                </span>
                {m.start_level > 0 && (
                  <span className={styles.resume}>
                    {(m.current_run ? t('mission.portal.continueAt') : t('mission.portal.checkpointAt'))
                      .replace('{n}', String(m.start_level + 1))}
                  </span>
                )}
              </div>
              {party && !party.is_leader && !m.current_run ? (
                <span className={styles.meta}>{t('mission.portal.leaderOnly')}</span>
              ) : (
                <button className={styles.enterBtn} disabled={entering !== null} onClick={() => void enter(m)}>
                  {entering === m.id ? '…' : m.current_run ? t('mission.portal.continue') : t('mission.portal.enter')}
                </button>
              )}
            </li>
          ))}
        </ul>

        {error && <p className={styles.error}>{error}</p>}
      </div>
    </div>,
    document.body,
  );
}
